import React, { useEffect, useState } from 'react';

import { EarningsDashboard, getEarningsDashboard } from '../api/dashboards';
import { formatMoney } from '../../../commodity/format/money';

const Tile: React.FC<{ label: string; value: string; sub?: string; color?: string }> = ({
  label,
  value,
  sub,
  color = '#222',
}) => (
  <div
    style={{
      border: '1px solid #d1d5da',
      borderRadius: 8,
      padding: 16,
      flex: '1 1 200px',
      minWidth: 200,
      background: '#fff',
    }}
  >
    <div style={{ fontSize: 12, color: '#666' }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 700, color, marginTop: 4 }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>{sub}</div>}
  </div>
);

/**
 * PI-3 Epic 4 S4.3 — earnings/settlement aggregate dashboard.
 * Totals + top-10 grossing teachers (sortable).
 */
const AdminEarningsDashboard: React.FC = () => {
  const [data, setData] = useState<EarningsDashboard | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getEarningsDashboard();
        if (!cancelled) setData(res);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'No se pudo cargar el dashboard de earnings');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) return <p style={{ color: '#c92a2a' }}>{error}</p>;
  if (!data) return <p>Cargando dashboard de earnings...</p>;

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 4px' }}>
      <h1 style={{ marginBottom: 4 }}>Earnings / Liquidación (plataforma)</h1>
      <p style={{ color: '#666', marginTop: 0, fontSize: 12 }}>
        Generado: {new Date(data.generatedAt).toLocaleString('es-MX')}
      </p>

      <h2>Totales</h2>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        <Tile
          label="Released 7d"
          value={formatMoney(data.totals.releasedRolling7d, data.currency)}
          color="#2b8a3e"
        />
        <Tile
          label="Released 30d"
          value={formatMoney(data.totals.releasedRolling30d, data.currency)}
          color="#2b8a3e"
        />
        <Tile
          label="Pending escrow"
          value={formatMoney(data.totals.pendingInEscrow, data.currency)}
          color="#1971c2"
        />
        <Tile
          label="Holdback in window"
          value={formatMoney(data.totals.holdbackInWindow, data.currency)}
          color="#e8590c"
        />
        <Tile
          label="Refunded total"
          value={formatMoney(data.totals.refundedTotal, data.currency)}
          color="#c92a2a"
        />
      </div>

      <h2 style={{ marginTop: 20 }}>Refunded by reason</h2>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        <Tile
          label="Cancellation"
          value={formatMoney(data.totals.refundedByReason.cancellation, data.currency)}
        />
        <Tile
          label="Dispute"
          value={formatMoney(data.totals.refundedByReason.dispute, data.currency)}
        />
        <Tile
          label="No-show"
          value={formatMoney(data.totals.refundedByReason.noShow, data.currency)}
        />
      </div>

      <h2 style={{ marginTop: 24 }}>Top 10 teachers (released 30d)</h2>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '2px solid #d1d5da' }}>
              <th style={{ padding: 6 }}>Teacher</th>
              <th>Released 30d</th>
              <th>Pending</th>
              <th>Holdback</th>
              <th>Refunded</th>
              <th>Enrollments</th>
            </tr>
          </thead>
          <tbody>
            {data.topTeachers.length === 0 && (
              <tr>
                <td colSpan={6} style={{ padding: 12, color: '#666' }}>
                  Sin datos todavía.
                </td>
              </tr>
            )}
            {data.topTeachers.map((t) => (
              <tr key={t.teacherId} style={{ borderBottom: '1px solid #e9ecef' }}>
                <td style={{ padding: 6, fontFamily: 'monospace', fontSize: 11 }}>
                  {t.teacherId.slice(0, 12)}
                </td>
                <td>{formatMoney(t.releasedRolling30d, data.currency)}</td>
                <td>{formatMoney(t.pendingInEscrow, data.currency)}</td>
                <td>{formatMoney(t.holdbackInWindow, data.currency)}</td>
                <td>{formatMoney(t.refundedTotal, data.currency)}</td>
                <td>{t.enrollmentCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminEarningsDashboard;
